import React, { createContext, useContext, useState, useEffect } from 'react';
import { collection, query, where, onSnapshot, addDoc, updateDoc, doc } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from './AuthContext';

const TaskNotificationContext = createContext();

export const useTaskNotifications = () => {
  const context = useContext(TaskNotificationContext);
  if (!context) {
    throw new Error('useTaskNotifications must be used within a TaskNotificationProvider');
  }
  return context;
};

export const TaskNotificationProvider = ({ children }) => {
  const { currentUser } = useAuth();
  const [taskNotifications, setTaskNotifications] = useState([]);
  const [unreadTaskCount, setUnreadTaskCount] = useState(0);

  useEffect(() => {
    if (!currentUser) return;

    // Lắng nghe task notifications cho user hiện tại
    const notificationsQuery = query(
      collection(db, 'taskNotifications'),
      where('userId', '==', currentUser.uid),
      where('read', '==', false)
    );

    const unsubscribe = onSnapshot(notificationsQuery, (snapshot) => {
      const notifications = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setTaskNotifications(notifications);
      setUnreadTaskCount(notifications.length);
    });

    return () => unsubscribe();
  }, [currentUser]);

  const createTaskNotification = async (userId, type, taskData, message) => {
    try {
      await addDoc(collection(db, 'taskNotifications'), {
        userId,
        type, // 'task_assigned', 'task_updated', 'task_completed'
        taskId: taskData.id,
        taskTitle: taskData.title,
        message,
        read: false,
        createdAt: new Date(),
        createdBy: currentUser.uid
      });
    } catch (error) {
      console.error('Lỗi khi tạo task notification:', error);
    }
  };

  const markNotificationAsRead = async (notificationId) => {
    try {
      await updateDoc(doc(db, 'taskNotifications', notificationId), {
        read: true,
        readAt: new Date()
      });
    } catch (error) {
      console.error('Lỗi khi đánh dấu notification đã đọc:', error);
    }
  };

  const markAllAsRead = async () => {
    try {
      const batch = taskNotifications.map(notification => 
        updateDoc(doc(db, 'taskNotifications', notification.id), {
          read: true,
          readAt: new Date()
        })
      );
      await Promise.all(batch);
    } catch (error) {
      console.error('Lỗi khi đánh dấu tất cả đã đọc:', error);
    }
  };

  const value = {
    taskNotifications,
    unreadTaskCount,
    createTaskNotification,
    markNotificationAsRead,
    markAllAsRead
  };

  return (
    <TaskNotificationContext.Provider value={value}>
      {children}
    </TaskNotificationContext.Provider>
  );
};
